import React from "react";
import { checkEnabled, normalizeColorKey, formatDate } from "../../utils/libraryUtils";
import { COLOR_VARIANTS } from "./studyGroupHubConfig";

const SharedReviewerCard = ({
  msg,
  group,
  user,
  openCardMenuId,
  setOpenCardMenuId,
  cardMenuRef,
  liveAvatars = {},
  memberAvatarMap = {},
  redeeming = false,
  onStudyAction,
  onViewHistory,
  onDeleteMessage,
  onLaunchChallenge,
}) => {
  const senderName =
    `${msg?.sender?.firstName || ""} ${msg?.sender?.lastName || ""}`.trim() || "Group Member";
  const date = formatDate(msg.createdAt) || new Date(msg.createdAt).toLocaleDateString();
  const titleText =
    msg?.reviewer?.title ||
    msg?.reviewer?.topic ||
    msg?.text?.replace("Shared Reviewer: ", "") ||
    "Untitled Reviewer";

  const rawColor = msg?.reviewer?.color || "blue";
  const theme = COLOR_VARIANTS[normalizeColorKey(rawColor)] || COLOR_VARIANTS.blue;

  const config = msg?.reviewer?.config || {};
  const showSummary = checkEnabled(config.summaryEnabled);
  const showFlashcards = checkEnabled(config.flashcardsEnabled);
  const showQuiz = checkEnabled(config.quizEnabled);

  const activeCount = (showSummary ? 1 : 0) + (showFlashcards ? 1 : 0) + (showQuiz ? 1 : 0);
  const cols = activeCount === 3 ? "grid-cols-3" : activeCount === 2 ? "grid-cols-2" : "grid-cols-1";
  const anyEnabled = activeCount > 0;
  const defaultMode = showSummary
    ? "summary"
    : showFlashcards
    ? "flashcards"
    : showQuiz
    ? "quiz"
    : "summary";

  const canDelete =
    user &&
    (String(group?.owner) === String(user._id) || String(msg.sender?._id) === String(user._id));
  const isVerified = Boolean(msg?.reviewer?.verified);
  const resolvedSenderAvatar =
    liveAvatars[String(msg?.sender?._id)] ||
    memberAvatarMap[String(msg?.sender?._id)] ||
    msg?.sender?.avatar;

  return (
    <article
      onClick={() => anyEnabled && onStudyAction(msg.reviewerCode, msg.reviewer?._id, defaultMode)}
      className={`group relative bg-white border ${theme.border} rounded-3xl flex flex-col justify-between shadow-sm hover:shadow-xl transition-all duration-300 border-t-4 hover:-translate-y-1 p-5 ${
        openCardMenuId === msg._id ? "z-30 overflow-visible" : "z-10 overflow-hidden"
      } ${anyEnabled ? "cursor-pointer" : "cursor-default opacity-80"}`}
    >
      {/* Soft ambient corner decorative glow */}
      <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
        <div
          className={`absolute -right-6 -top-6 w-24 h-24 ${
            theme.glow || "bg-blue-50"
          } rounded-full transition-transform duration-500 group-hover:scale-125 opacity-60`}
        />
      </div>

      <div className={`relative ${openCardMenuId === msg._id ? "z-40" : "z-20"}`}>
        {/* Header Section: Icon, Badges & Menu */}
        <div className="flex justify-between items-start mb-3.5">
          <div
            className={`w-10 h-10 rounded-2xl ${theme.iconBg} ${theme.iconText} flex items-center justify-center text-lg shadow-xs transition-transform group-hover:scale-105 duration-200`}
          >
            <i className="fa-solid fa-file-lines"></i>
          </div>

          <div className="flex items-center gap-1.5">
            <span
              className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md border flex items-center gap-1 uppercase tracking-wider ${theme.badge}`}
              title="Shared in this Hub"
            >
              <i className="fa-solid fa-share-nodes text-[9px]"></i>
              <span>SHARED</span>
            </span>

            {isVerified && (
              <span
                className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/80 flex items-center gap-1 uppercase tracking-wider shadow-xs"
                title="Verified on Blockchain"
              >
                <i className="fa-solid fa-circle-check text-[9px] text-emerald-600"></i>
                <span>Verified</span>
              </span>
            )}

            {/* 3-Dot Dropdown Menu */}
            <div className="relative" ref={openCardMenuId === msg._id ? cardMenuRef : null}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenCardMenuId(openCardMenuId === msg._id ? null : msg._id);
                }}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition ${
                  openCardMenuId === msg._id ? "bg-gray-100 text-gray-700" : ""
                }`}
                title="More options"
              >
                <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
              </button>

              {openCardMenuId === msg._id && (
                <div
                  className="absolute right-0 top-8 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150 origin-top-right p-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col gap-0.5">
                    {msg.reviewer?._id && showQuiz && (
                      <>
                        <button
                          type="button"
                          onClick={() => onViewHistory(msg.reviewer._id, titleText)}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition"
                        >
                          <i className="fa-solid fa-clock-rotate-left text-blue-500 text-xs w-4 text-center"></i>
                          <span>Attempt History</span>
                        </button>

                        {onLaunchChallenge && (
                          <button
                            type="button"
                            onClick={() => {
                              setOpenCardMenuId(null);
                              onLaunchChallenge(msg.reviewer._id);
                            }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-50 rounded-xl transition"
                          >
                            <i className="fa-solid fa-bolt text-purple-600 text-xs w-4 text-center"></i>
                            <span>Launch Challenge</span>
                          </button>
                        )}
                      </>
                    )}

                    {canDelete && (
                      <>
                        {showQuiz && <div className="border-t border-gray-100 my-1" />}
                        <button
                          type="button"
                          onClick={() => {
                            setOpenCardMenuId(null);
                            onDeleteMessage(msg._id);
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl transition"
                        >
                          <i className="fa-solid fa-trash text-red-500 text-xs w-4 text-center"></i>
                          <span>Remove Reviewer</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <h3
          className="font-bold text-gray-900 mb-1.5 line-clamp-2 text-base leading-snug tracking-tight group-hover:text-blue-600 transition-colors"
          title={titleText}
        >
          {titleText}
        </h3>

        {/* Sender & Date Metadata */}
        <div className="flex items-center gap-2 text-[11px] text-gray-500 font-medium mb-4 truncate">
          <div className="flex items-center gap-1.5 truncate max-w-[140px]">
            {resolvedSenderAvatar ? (
              <img
                src={resolvedSenderAvatar}
                alt={senderName}
                className="w-4 h-4 rounded-full object-cover border border-gray-200 shrink-0"
              />
            ) : (
              <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[9px] shrink-0">
                {senderName.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-gray-700 truncate">{senderName}</span>
          </div>
          <span>•</span>
          <div className="flex items-center gap-1 shrink-0 text-gray-400">
            <i className="fa-regular fa-calendar text-[10px]"></i>
            <span>{date}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons Footer */}
      <div className="relative z-0 pt-3 border-t border-gray-100">
        <div className={`grid ${cols} gap-2`}>
          {showSummary && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStudyAction(msg.reviewerCode, msg.reviewer?._id, "summary");
              }}
              disabled={redeeming}
              className={`flex flex-col items-center justify-center py-2.5 rounded-xl text-[10px] font-bold transition gap-1 active:scale-95 shadow-xs ${theme.btn}`}
              title="Read Summary Notes"
            >
              <i className="fa-solid fa-align-left text-xs"></i>
              <span>Summary</span>
            </button>
          )}

          {showFlashcards && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStudyAction(msg.reviewerCode, msg.reviewer?._id, "flashcards");
              }}
              disabled={redeeming}
              className={`flex flex-col items-center justify-center py-2.5 rounded-xl text-[10px] font-bold transition gap-1 active:scale-95 shadow-xs ${theme.btn}`}
              title="Practice Flashcards"
            >
              <i className="fa-solid fa-layer-group text-xs"></i>
              <span>Cards</span>
            </button>
          )}

          {showQuiz && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStudyAction(msg.reviewerCode, msg.reviewer?._id, "quiz");
              }}
              disabled={redeeming}
              className={`flex flex-col items-center justify-center py-2.5 rounded-xl text-[10px] font-bold transition gap-1 active:scale-95 shadow-xs ${theme.btn}`}
              title="Take Practice Quiz"
            >
              <i className="fa-solid fa-bolt text-xs"></i>
              <span>Quiz</span>
            </button>
          )}

          {!anyEnabled && (
            <button
              type="button"
              disabled
              className="col-span-3 flex items-center justify-center py-2.5 rounded-xl text-[10px] font-bold text-gray-400 bg-gray-50 border border-gray-100 cursor-not-allowed gap-1.5"
            >
              <i className="fa-solid fa-ban text-xs"></i>
              <span>No Study Modes Active</span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default SharedReviewerCard;
